import PageLoader from "components/PageLoader"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import { Plus, Pencil, Search, Tag } from "lucide-react"
import { useBrandsQuery } from "hooks/query/useBrandsQuery"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Separator } from "@/components/ui/separator"

const PAGE_SIZE_OPTIONS = [30, 50, 100] as const

export const BrandsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: brands, isLoading } = useBrandsQuery()

  const [search, setSearch] = useState("")
  const [pageSize, setPageSize] = useState<number>(30)
  const [currentPage, setCurrentPage] = useState(1)

  // Filter brands by search
  const filteredBrands = useMemo(() => {
    if (!search.trim()) return brands
    const q = search.toLowerCase()
    return brands.filter(
      (b) => b.name.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q)
    )
  }, [brands, search])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredBrands.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedBrands = filteredBrands.slice((safePage - 1) * pageSize, safePage * pageSize)

  // Reset to page 1 when search or pageSize changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setCurrentPage(1)
  }

  if (isLoading) return <PageLoader />

  return (
    <div className="p-6" data-testid="brands-page">
      {/* ──── Header ──── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="brands-title">
            {t("brandsPage.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("brandsPage.subtitle", { count: brands.length })}
          </p>
        </div>
        <Button size="sm" asChild>
          <RouterLink to="/brands/add" data-testid="brands-add">
            <Plus className="mr-1.5 size-3.5" />
            {t("brandsPage.addBrand")}
          </RouterLink>
        </Button>
      </div>

      <Separator className="my-6" />

      {/* ──── Toolbar: search + page size ──── */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="brands-search"
            placeholder={t("brandsPage.searchPlaceholder", {
              defaultValue: "Search by name or slug...",
            })}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="whitespace-nowrap">Rows per page</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]" data-testid="brands-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ──── Table ──── */}
      {paginatedBrands.length > 0 ? (
        <Card data-testid="brands-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium">{t("brandsPage.brandName")}</TableHead>
                <TableHead className="font-medium">{t("brandsPage.brandSlug")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedBrands.map((brand) => (
                <TableRow
                  key={brand.id || brand.name}
                  data-testid="brand-row"
                  data-brand-id={brand.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/brands/edit?id=${brand.id}`)}
                >
                  <TableCell>
                    <span className="font-medium text-foreground">{brand.name}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{brand.slug}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("brandsPage.editLabel")}
                      data-testid="brand-edit"
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* ──── Pagination footer ──── */}
          {totalPages > 1 && (
            <>
              <Separator />
              <div className="flex items-center justify-between px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  {(safePage - 1) * pageSize + 1}–
                  {Math.min(safePage * pageSize, filteredBrands.length)} of {filteredBrands.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      ) : (
        <Card className="border-dashed" data-testid="brands-empty-state">
          <CardContent className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-12">
            <Tag className="size-10 text-muted-foreground/50" />
            <div className="text-center">
              <h3 className="font-semibold">
                {search ? "No brands match your search" : "No brands yet"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {search ? `No results for "${search}"` : "Get started by adding your first brand."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
